import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Sheet } from '@node-space/ui-components'
import type { CreateCustomerAgreement } from 'types/customers'
import type { ErrorDetails } from 'types/types'
import { AddCustomerAgreementConfirmDetails } from './AddCustomerAgreementConfirmDetails'
import { AddCustomerAgreementForm } from './AddCustomerAgreementForm'

export interface AddCustomerAgreementProps {
  isOpen: boolean
  onClose: () => void
  onCustomerAdded: () => void
}

type ConfirmState = {
  formData: CreateCustomerAgreement
  displayData: Partial<Record<keyof CreateCustomerAgreement, string>>
}

export const AddCustomerAgreement = ({
  isOpen,
  onClose,
  onCustomerAdded,
}: AddCustomerAgreementProps) => {
  const { t } = useTranslation()
  const [isConfirmView, setIsConfirmView] = useState(false)
  const [confirmState, setConfirmState] = useState<ConfirmState>()
  const [serverErrors, setServerErrors] = useState<ErrorDetails | undefined>()

  const onConfirmDetails = (
    formData: CreateCustomerAgreement,
    displayData: Partial<Record<keyof CreateCustomerAgreement, string>>
  ) => {
    setConfirmState({ formData, displayData })
    setIsConfirmView(true)
  }

  const reset = () => {
    setConfirmState(undefined)
    setIsConfirmView(false)
    setServerErrors(undefined)
  }

  const onBack = () => {
    if (isConfirmView) {
      setIsConfirmView(false)
    } else {
      reset()
      onClose()
    }
  }

  const onCloseSheet = () => {
    reset()
    onClose()
  }

  const onCancel = () => {
    reset()
    onClose()
  }

  const onSuccess = () => {
    reset()
    onClose()
    onCustomerAdded()
  }

  const onValidationError = (errors: ErrorDetails) => {
    setServerErrors(errors)
  }

  return (
    <Sheet
      isOpen={isOpen}
      onClose={onCloseSheet}
      header={t(isConfirmView ? 'customers.confirmDetails' : 'customers.newBusinessCustomer')}
      onBack={onBack}
    >
      <Box width="full" paddingY={24}>
        {isConfirmView && confirmState ? (
          <AddCustomerAgreementConfirmDetails
            formData={confirmState.formData}
            displayData={confirmState.displayData}
            onSuccess={onSuccess}
            onCancel={onCancel}
            onValidationError={onValidationError}
          />
        ) : (
          <AddCustomerAgreementForm
            defaultValues={confirmState?.formData}
            serverErrors={serverErrors}
            onConfirm={onConfirmDetails}
            onCancel={onCancel}
          />
        )}
      </Box>
    </Sheet>
  )
}
